import { Search, SentimentDissatisfied } from "@mui/icons-material";
import Cart, { generateCartItemsFrom } from "./Cart";
import "./Cart.css";
import {
  Button,
  CircularProgress,
  Grid,
  InputAdornment,
  TextField,
} from "@mui/material";
import { Box } from "@mui/system";
import { Typography } from "@mui/material";
import axios from "axios";
import { useSnackbar } from "notistack";
import React, { useEffect, useState } from "react";
import { config } from "../App";
import Footer from "./Footer";
import Header from "./Header";
import "./Products.css";
import ProductCard from "./ProductCard";

/**
 * @typedef {Object} CartItem -  - Data on product added to cart
 *
 * @property {string} name - The name or title of the product in cart
 * @property {string} qty - The quantity of product added to cart
 * @property {string} category - The category that the product belongs to
 * @property {number} cost - The price to buy the product
 * @property {number} rating - The aggregate rating of the product (integer out of five)
 * @property {string} image - Contains URL for the product image
 * @property {string} productId - Unique ID for the product
 */

const Products = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [searchList, setSearchList] = useState("");
  const [productLists, setProductList] = useState([]);
  const [dataIsLoading, setDataIsLoading] = useState(true);
  const [isDataAvailable, setIsDataAvailable] = useState(true);
  const [debounceTimeout, setDebounceTimeout] = useState(0);

  const [cartItem, setCartItem] = useState([]);

  const token = localStorage.getItem("token");

  function handleChange(e) {
    setSearchList(e.target.value);
  }

  // TODO: CRIO_TASK_MODULE_PRODUCTS - Fetch products data and store it
  /**
   * Make API call to get the products list and store it to display the products
   *
   * @returns { Array.<Product> }
   *      Array of objects with complete data on all available products
   *
   * API endpoint - "GET /products"
   *
   * Example for successful response from backend:
   * HTTP 200
   * [
   *      {
   *          "name": "iPhone XR",
   *          "category": "Phones",
   *          "cost": 100,
   *          "rating": 4,
   *          "image": "https://i.imgur.com/lulqWzW.jpg",
   *          "_id": "v4sLtEcMpzabRyfx"
   *      },
   *      {
   *          "name": "Basketball",
   *          "category": "Sports",
   *          "cost": 100,
   *          "rating": 5,
   *          "image": "https://i.imgur.com/lulqWzW.jpg",
   *          "_id": "upLK9JbQ4rMhTwt4"
   *      }
   * ]
   *
   * Example for failed response from backend:
   * HTTP 500
   * {
   *      "success": false,
   *      "message": "Something went wrong. Check the backend console for more details"
   * }
   */
  const performAPICall = async () => {
    try {
      setDataIsLoading(true);
      let res = await axios.get(`${config.endpoint}/products`);
      console.log(res.data);
      setProductList(res.data);
      // performSearch(searchList);
      setDataIsLoading(false);
    } catch (error) {
      setIsDataAvailable(false);
      if (error.response) {
        enqueueSnackbar(
          error.response.data.message || ":( Oops No Products Found",
          { variant: "error" }
        );
      } else {
        enqueueSnackbar(
          "Something went wrong. Check that the backend is running.",
          { variant: "error" }
        );
      }
    }
  };

  useEffect(() => {
    performAPICall();
  }, []);

  useEffect(() => {
    debounceSearch(debounceTimeout);
  }, [searchList]);

  // TODO: CRIO_TASK_MODULE_PRODUCTS - Implement search logic
  /**
   * Definition for search handler
   * This is the function that is called on adding new search keys
   *
   * @param {string} text
   *    Text user types in the search bar. To filter the displayed products based on this text.
   *
   * @returns { Array.<Product> }
   *      Array of objects with complete data on filtered set of products
   *
   * API endpoint - "GET /products/search?value=<search-query>"
   *
   */
  const performSearch = async (searchList) => {
    // GET /products/search?value=<search-string>
    let url = `${config.endpoint}/products`;
    if (searchList !== "") {
      url = `${url}/search?value=${searchList}`;
    }
    try {
      setIsDataAvailable(true);
      setDataIsLoading(true);
      let res = await axios.get(url);
      console.log(res.data);
      setProductList(res.data);
      setDataIsLoading(false);
      setIsDataAvailable(true);
    } catch (error) {
      setIsDataAvailable(false);
      if (error.response) {
        enqueueSnackbar(
          error.response.data.message || ":( Oops No Products Found",
          { variant: "error" }
        );
      } else {
        enqueueSnackbar(
          "Something went wrong. Check that the backend is running.",
          { variant: "error" }
        );
      }
    }
  };

  // TODO: CRIO_TASK_MODULE_PRODUCTS - Optimise API calls with debounce search implementation
  /**
   * Definition for debounce handler
   * With debounce, this is the function to be called whenever the user types text in the searchbar field
   *
   * @param {{ target: { value: string } }} event
   *    JS event object emitted from the search input field
   *
   * @param {NodeJS.Timeout} debounceTimeout
   *    Timer id set for the previous debounce call
   *
   */
  const debounceSearch = (debounceTimeout) => {
    // const value = event.target.value
    // event, debounceTimeout
    // setSearchList(event.target.value);
    clearTimeout(debounceTimeout);
    const newTime = setTimeout(() => {
      performSearch(searchList);
    }, 500);
    setDebounceTimeout(newTime);
  };

  /**
   * Perform the API call to fetch the user's cart and return the response
   *
   * @param {string} token - Authentication token returned on login
   *
   * @returns { Array.<{ productId: string, qty: number }> | null }
   *    The response JSON object
   *
   * Example for successful response from backend:
   * HTTP 200
   * [
   *      {
   *          "productId": "KCRwjF7lN97HnEaY",
   *          "qty": 3
   *      },
   *      {
   *          "productId": "BW0jAAeDJmlZCF8i",
   *          "qty": 1
   *      }
   * ]
   *
   * Example for failed response from backend:
   * HTTP 401
   * {
   *      "success": false,
   *      "message": "Protected route, Oauth2 Bearer token not found"
   * }
   */
  const fetchCart = async (token) => {
    if (!token) return;

    try {
      // TODO: CRIO_TASK_MODULE_CART - Pass Bearer token inside "Authorization" header to get data from "GET /cart" API and return the response data
      let res = await axios.get(`${config.endpoint}/cart`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      console.log(res.data);

      return res.data;
    } catch (e) {
      if (e.response && e.response.status === 400) {
        enqueueSnackbar(e.response.data.message, { variant: "error" });
      } else {
        enqueueSnackbar(
          "Could not fetch cart details. Check that the backend is running, reachable and returns valid JSON.",
          {
            variant: "error",
          }
        );
      }
      return null;
    }
  };

  // useEffect(()=>{fetchCart(token)},[]);

  useEffect(() => {
    fetchCart(token)
      .then((cartData) => generateCartItemsFrom(cartData, productLists))
      .then((cartItems) => setCartItem(cartItems));
  }, [productLists]);

  // TODO: CRIO_TASK_MODULE_CART - Return if a product already exists in the cart
  /**
   * Return if a product already is present in the cart
   *
   * @param { Array.<{ productId: String, quantity: Number }> } items
   *    Array of objects with productId and quantity of products in cart
   * @param { String } productId
   *    Id of a product to be checked
   *
   * @returns { Boolean }
   *    Whether a product of given "productId" exists in the "items" array
   *
   */
  const isItemInCart = (cartItem, productId) => {
    return cartItem.findIndex((item) => item.productId === productId) !== -1;
  };

  /**
   * Perform the API call to add or update items in the user's cart and update local cart data to display the latest cart
   *
   * @param {string} token
   *    Authentication token returned on login
   * @param { Array.<{ productId: String, quantity: Number }> } items
   *    Array of objects with productId and quantity of products in cart
   * @param { Array.<Product> } products
   *    Array of objects with complete data on all available products
   * @param {string} productId
   *    ID of the product that is to be added or updated in cart
   * @param {number} qty
   *    How many of the product should be in the cart
   * @param {boolean} options
   *    If this function was triggered from the product card's "Add to Cart" button
   *
   * Example for successful response from backend:
   * HTTP 200 - Updated list of cart items
   * [
   *      {
   *          "productId": "KCRwjF7lN97HnEaY",
   *          "qty": 3
   *      },
   *      {
   *          "productId": "BW0jAAeDJmlZCF8i",
   *          "qty": 1
   *      }
   * ]
   *
   * Example for failed response from backend:
   * HTTP 404 - On invalid productId
   * {
   *      "success": false,
   *      "message": "Product doesn't exist"
   * }
   */
  const addToCart = async (
    token,
    cartItem,
    productId,
    productLists,
    qty,
    options = { preventDuplicate: false }
  ) => {
    if (!token) {
      enqueueSnackbar("Please log in to add item to cart", {
        variant: "warning",
      });
      return;
    }
    if (options.preventDuplicate && isItemInCart(cartItem, productId)) {
      enqueueSnackbar(
        "Item already in cart. Use the cart sidebar to update quantity or remove item.",
        { variant: "warning" }
      );
      return;
    }

    try {
      const res = await axios.post(
        `${config.endpoint}/cart`,
        { productId, qty },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      const cartItems = generateCartItemsFrom(res.data, productLists);
      setCartItem(cartItems);
    } catch (e) {
      if (e.response) {
        enqueueSnackbar(e.response.data.message, { variant: "error" });
      } else {
        enqueueSnackbar(
          "Could not fetch products. Check that the backend is running, reachabe and returns valid JSON.",
          {
            variant: "error",
          }
        );
      }
    }
  };

  return (
    <div>
      <Header>
        {/* TODO: CRIO_TASK_MODULE_PRODUCTS - Display search bar in the header for Products page */}
        <TextField
          onChange={handleChange}
          // onchange={(event)=>{debounceSearch(event,debounceTimeout)}}
          value={searchList}
          variant="outlined"
          className="search-desktop"
          size="small"
          // fullWidth
          InputProps={{
            className: "search",
            endAdornment: (
              <InputAdornment position="end">
                <Search color="primary" />
              </InputAdornment>
            ),
          }}
          placeholder="Search for items/categories"
          name="search"
        />
      </Header>

      {/* Search view for mobiles */}
      <TextField
        onChange={(e) => handleChange(e)}
        // onchange={(event)=>{debounceSearch(event,debounceTimeout)}}
        value={searchList}
        className="search-mobile"
        size="small"
        fullWidth
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <Search color="primary" />
            </InputAdornment>
          ),
        }}
        placeholder="Search for items/categories"
        name="search"
      />

      <Grid container justifyContent="center" spacing={1}>
        {/* //Grid item 1 */}
        <Grid md={token ? "8" : "12"} xs={12} item className="product-grid">
          <Box className="hero">
            <p className="hero-heading">
              India’s <span className="hero-highlight">FASTEST DELIVERY</span>{" "}
              to your door step
            </p>
          </Box>

          {isDataAvailable && !dataIsLoading ? (
            <Grid spacing={2} container>
              {productLists.map((productList) => {
                const {
                  name,
                  category,
                  cost,
                  rating,
                  image,
                  _id,
                } = productList;
                return (
                  <Grid
                    mt={2}
                    // direction="row"
                    justifyContent="center"
                    alignItems="center"
                    item
                    xs={12}
                    sm={6}
                    md={3}
                    key={_id}
                  >
                    <ProductCard
                      image={image}
                      name={name}
                      category={category}
                      cost={`$${cost}`}
                      rating={rating}
                      handleAddToCart={async () =>
                        await addToCart(token, cartItem, _id, productLists, 1, {
                          preventDuplicate: true,
                        })
                      }
                    />
                  </Grid>
                );
              })}
            </Grid>
          ) : (
            <Grid
              mt={10}
              mb={10}
              container
              direction="column"
              justifyContent="center"
              alignItems="center"
            >
              {isDataAvailable && dataIsLoading ? (
                <>
                  <CircularProgress />
                  <Typography component="h5">Loading Products</Typography>
                </>
              ) : (
                <>
                  <SentimentDissatisfied />
                  <Typography component="h5">No products found</Typography>
                </>
              )}
            </Grid>
          )}
        </Grid>

        {/* Grid item 2 */}

        {token && (
          <Grid item bg="#E9F5E1" xs={12} md={3} style={{ margin: "20px" }} item>
            <Cart
              products={productLists}
              items={cartItem}
              handleQuantity={addToCart}
            />
          </Grid>
        )}
      </Grid>
      <Footer />
    </div>
  );
};

export default Products;
